import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ChevronDown,
  Download,
  FileSpreadsheet,
  Inbox,
  Loader2,
} from "lucide-react";
import { FaSyncAlt } from "react-icons/fa";
import { HiOutlineArrowLongLeft, HiOutlineArrowLongRight } from "react-icons/hi2";
import AppContext from "../../context/AppContext";
import translations from "../../components/common/Translations";
import ReactSelector from "../../components/common/CommonReactSelector";
import CommonButton from "../../components/common/CommonButton";
import PageSizeSelector from "../../components/common/PageSizeSelector";
import PagerArrowButton from "../../components/common/PagerArrowButton";
import TableScrollShell from "../../components/common/TableScrollShell";
import FailedAccountsPanel from "../../components/pdfExport/FailedAccountsPanel";
import BulkExportProgressCard from "../../components/pdfExport/BulkExportProgressCard";
import {
  clampPercentage,
  fillTemplate,
  formatBulkExportDateRange,
  formatBulkExportTimestamp,
  formatFileSize,
  getBulkExportStatusMeta,
  isBulkExportActive,
  openBulkExportFile,
} from "../../components/pdfExport/bulkExportUtils";
import { BULK_PDF_EXPORT_CODE, getBulkPdfExportList } from "../../service/Task";
import { getActivityListByProjectId } from "../../service/User";
import { getPermittedActivities } from "../../utils/activityPermissions";
import useBulkPdfExport, { BULK_PDF_EXPORT_PHASE } from "../../hooks/useBulkPdfExport";
import useDirection from "../../hooks/useDirection";
import { downloadFailedAccountsExcel } from "../../utils/failedAccountsExport";
import { getPermissionByScreenCode } from "../../utils/permissions";
import { toast } from "../../utils/toast";

/** While any listed export is still queued/running the list refreshes itself. */
const ACTIVE_LIST_REFRESH_MS = 5000;

/**
 * Bulk PDF export history (BLF-2171, item 8).
 *
 * The backend job outlives the page that started it, so this screen is how a
 * user who navigated away (or followed the completion notification) gets back
 * to their ZIP, with the date range, account numbers and workflow that
 * produced it.
 */
const PdfExportHistory = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme, langMode, projectId } = useContext(AppContext);
  const { isRTL: isRtl, dir } = useDirection();
  const t = translations[langMode] ?? {};

  const pdfReportPerm = getPermissionByScreenCode("PDF_REPORT");
  const hasPDFViewAccess = Boolean(pdfReportPerm?.canView);
  const hasPDFDownloadAccess = Boolean(
    pdfReportPerm?.canCreate || pdfReportPerm?.canUpdate || pdfReportPerm?.canDelete
  );

  const [exportList, setExportList] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [activity, setActivity] = useState([]);
  const [workflowFilter, setWorkflowFilter] = useState(null);
  const [expandedExportId, setExpandedExportId] = useState(null);
  // The export whose live progress the card at the top of the screen follows:
  // set from `?exportId=` (the hand-off from Export PDF, and the completion
  // notification deep-link) or by picking a row's Track action.
  const [focusedExportId, setFocusedExportId] = useState(() => {
    const raw = searchParams.get("exportId");
    const parsed = Number(raw);
    return raw && !Number.isNaN(parsed) ? parsed : null;
  });

  const deepLinkedExportId = searchParams.get("exportId");
  const { phase, record: trackedRecord, message: trackedMessage, track, reset } =
    useBulkPdfExport();

  const totalPages = Math.max(Math.ceil(totalValue / pageSize) - 1, 0);
  const startIndex = currentPage * pageSize;
  const displayStart = totalValue > 0 ? startIndex + 1 : 0;
  const displayEnd = totalValue > 0 ? Math.min(startIndex + pageSize, totalValue) : 0;

  const loadExports = useCallback(
    async ({ silent = false } = {}) => {
      if (!hasPDFViewAccess) return;
      if (!silent) setIsLoading(true);
      try {
        const { code, value } = await getBulkPdfExportList({
          page: currentPage,
          size: pageSize,
          workflowId: workflowFilter?.value ?? null,
        });
        if (code === BULK_PDF_EXPORT_CODE.SUCCESS && value) {
          setExportList(Array.isArray(value.exportList) ? value.exportList : []);
          setTotalValue(Number(value.count) || 0);
          setLoadError("");
        } else if (code === BULK_PDF_EXPORT_CODE.NO_DATA) {
          setExportList([]);
          setTotalValue(0);
          setLoadError("");
        } else {
          setLoadError(typeof value === "string" ? value : "");
        }
      } catch (error) {
        console.error("Error fetching bulk PDF export history:", error);
        setLoadError(
          error?.response?.data?.body?.value ??
            (t.bulkExportHistoryLoadError ?? "Could not load export history.")
        );
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentPage, pageSize, workflowFilter, hasPDFViewAccess]
  );

  useEffect(() => {
    loadExports();
  }, [loadExports]);

  const hasActiveExport = useMemo(
    () => exportList.some((item) => isBulkExportActive(item?.status)),
    [exportList]
  );

  useEffect(() => {
    if (!hasActiveExport) return undefined;
    const timer = setInterval(() => loadExports({ silent: true }), ACTIVE_LIST_REFRESH_MS);
    return () => clearInterval(timer);
  }, [hasActiveExport, loadExports]);

  useEffect(() => {
    const fetchActivityList = async () => {
      try {
        const data = await getActivityListByProjectId(projectId);
        setActivity(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching activity list:", error);
      }
    };
    if (projectId) fetchActivityList();
  }, [projectId]);

  useEffect(() => {
    if (!deepLinkedExportId) return;
    const parsed = Number(deepLinkedExportId);
    if (Number.isNaN(parsed)) return;
    setFocusedExportId((previous) => (previous === parsed ? previous : parsed));
  }, [deepLinkedExportId]);

  // Poll /status for the focused export. Seeded from the row the list already
  // holds when there is one, so the card renders filled in rather than empty
  // for the first poll.
  useEffect(() => {
    if (focusedExportId == null) {
      reset();
      return;
    }
    const seed = exportList.find((item) => item?.exportId === focusedExportId) ?? null;
    track(focusedExportId, seed);
    // Deliberately not keyed on exportList: re-seeding on every list refresh
    // would restart the poll loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedExportId]);

  // Once the tracked export settles, pull the list again so its row stops
  // showing stale progress.
  const syncedTerminalPhaseRef = useRef(null);
  useEffect(() => {
    if (phase !== BULK_PDF_EXPORT_PHASE.COMPLETED && phase !== BULK_PDF_EXPORT_PHASE.FAILED) return;
    const key = `${focusedExportId}:${phase}`;
    if (syncedTerminalPhaseRef.current === key) return;
    syncedTerminalPhaseRef.current = key;
    loadExports({ silent: true });
  }, [phase, focusedExportId, loadExports]);

  const permittedActivity = useMemo(() => {
    return getPermittedActivities(activity);
  }, [activity]);

  const activityOptions = useMemo(
    () => [
      { value: null, label: t.bulkExportAllActivities ?? "All activities" },
      ...(permittedActivity || [])
        .filter(Boolean)
        .map((item) => ({ value: item.workflowId, label: item.workflowName })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permittedActivity, langMode]
  );

  const handleDownload = (item) => {
    if (!hasPDFDownloadAccess) return;
    if (!openBulkExportFile(item?.fileUrl)) {
      toast.error(t.bulkExportNoFile ?? "This export finished without a file. Please try again.");
    }
  };

  const focusExport = (exportId) => {
    setFocusedExportId(exportId);
    const next = new URLSearchParams(searchParams);
    next.set("exportId", String(exportId));
    setSearchParams(next, { replace: true });
  };

  const dismissFocusedExport = () => {
    setFocusedExportId(null);
    if (!searchParams.get("exportId")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("exportId");
    setSearchParams(next, { replace: true });
  };

  const themeClass =
    theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : "";

  const columnCount = 9;

  return (
    <div
      className="flex min-h-full w-full min-w-0 flex-col overflow-y-auto bg-white sm:h-full sm:min-h-0 sm:overflow-hidden"
      dir={dir}
    >
      <div className={`box-border flex min-h-full w-full min-w-0 flex-col bg-white sm:h-full sm:min-h-0 ${themeClass}`}>
        <div
          className={`mx-2 mt-2 flex min-w-0 shrink-0 flex-col gap-3 rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm sm:mx-4 sm:mt-3 sm:gap-4 sm:px-5 sm:py-4 ${themeClass}`}
        >
          {/* No `flex-row-reverse` for RTL: the page root already carries
              dir="rtl", so a plain flex row is laid out right-to-left and
              reversing it again would push the title back to the LTR side. */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 flex-1 items-start gap-2">
              <button
                type="button"
                onClick={() => navigate("/pdf-generator")}
                aria-label={t.bulkExportBackToPdf ?? "Back to Export PDF"}
                className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-800 shadow-sm transition hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                {/* "Back" points towards the start of the reading direction. */}
                {isRtl ? <HiOutlineArrowLongRight size={22} /> : <HiOutlineArrowLongLeft size={22} />}
              </button>
              <div className="min-w-0">
                <h1 className="text-base font-bold leading-tight text-slate-800 sm:text-lg">
                  {t.bulkExportHistoryTitle ?? "Export History"}
                </h1>
                <p className="mt-0.5 text-xs text-slate-500">
                  {t.bulkExportHistorySubtitle ??
                    "Every bulk PDF export you have requested, newest first."}
                </p>
              </div>
            </div>

            <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto">
              <div className="min-w-0 flex-1 sm:w-[219px] sm:flex-none">
                <ReactSelector
                  options={activityOptions}
                  value={workflowFilter ?? activityOptions[0]}
                  onChange={(option) => {
                    setWorkflowFilter(option?.value ? option : null);
                    setCurrentPage(0);
                  }}
                  placeholder={t.Activities ?? "Activities"}
                  isDisabled={!hasPDFViewAccess}
                  height="40px"
                />
              </div>
              {/* Same refresh control as the Upload Task screen's table. */}
              <CommonButton colorIntent="primary"
                onClick={() => loadExports()}
                variant="primary"
                theme={theme}
                type="button"
                disabled={!hasPDFViewAccess || isLoading}
                wrapperClassName="shrink-0 sm:w-auto"
                className="!h-9 !w-9 sm:!w-auto !p-0 sm:!px-4 !justify-center"
                aria-label={t.refresh ?? "Refresh Table"}
                data-testid="pdf-export-history-refresh-button"
              >
                <FaSyncAlt size={15} className={isLoading ? "animate-spin" : "shrink-0"} />
                <span className="hidden sm:inline whitespace-nowrap">{t.refresh ?? "Refresh Table"}</span>
              </CommonButton>
            </div>
          </div>

          {loadError && (
            <p className="rounded-lg border border-red-200 bg-red-50/70 px-3 py-2 text-[11px] text-red-700">
              {loadError}
            </p>
          )}

          {focusedExportId != null && (
            <BulkExportProgressCard
              phase={phase}
              record={trackedRecord}
              message={trackedMessage}
              langMode={langMode}
              onDismiss={dismissFocusedExport}
              canDownload={hasPDFDownloadAccess}
            />
          )}
        </div>

        <div className="flex w-full min-h-0 flex-1 flex-col">
          <div
            className={`mx-2 mb-2 mt-2 flex min-h-[22rem] min-w-0 flex-none flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:mx-4 sm:mb-3 sm:mt-3 sm:min-h-0 sm:flex-1 ${themeClass}`}
          >
            <div className={`scrollbar-container relative z-20 flex flex-wrap items-center justify-between overflow-visible border-b border-slate-100 bg-white px-3 py-2.5 sm:px-5 ${theme === "dark" ? "border-gray-600" : ""}`}>
              <div className="flex w-full items-center justify-between gap-2">
                <p className="flex items-center gap-3 text-xs text-slate-500 sm:gap-4 sm:whitespace-nowrap">
                  <span className="rtl-keep-ltr inline sm:hidden">
                    {`${displayStart}-${displayEnd} / ${totalValue}`}
                  </span>
                  <span className="hidden sm:inline">
                    {fillTemplate(t.bulkExportShowingRange ?? "Showing {start}-{end} of {total}", {
                      start: displayStart,
                      end: displayEnd,
                      total: totalValue,
                    })}
                  </span>
                </p>
                <div className="flex min-w-0 items-center gap-2 text-xs text-slate-500 sm:gap-3">
                  <div className="flex items-center gap-1.5">
                    <label htmlFor="pageSize" className="hidden sm:block sm:whitespace-nowrap">
                      {t.rows ?? "Rows"}
                    </label>
                    <PageSizeSelector
                      value={pageSize}
                      onChange={(size) => {
                        setPageSize(size);
                        setCurrentPage(0);
                      }}
                      disabled={!hasPDFViewAccess}
                      height="32px"
                      fontSize="12px"
                    />
                  </div>
                  <span className="pager-nav flex items-center gap-1">
                    <PagerArrowButton
                      direction="prev"
                      isRtl={isRtl}
                      onClick={() => setCurrentPage((page) => Math.max(page - 1, 0))}
                      disabled={!hasPDFViewAccess || currentPage === 0}
                      ariaLabel={t.previousPage ?? "Previous page"}
                    />
                    <span className="whitespace-nowrap px-1 text-xs text-slate-600">
                      {currentPage + 1} / {Math.max(totalPages + 1, 1)}
                    </span>
                    <PagerArrowButton
                      direction="next"
                      isRtl={isRtl}
                      onClick={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
                      disabled={!hasPDFViewAccess || currentPage >= totalPages}
                      ariaLabel={t.nextPage ?? "Next page"}
                    />
                  </span>
                </div>
              </div>
            </div>

            <TableScrollShell className="w-full flex-1 min-h-0">
              <table
                className={`w-full min-w-[1140px] text-left text-xs text-gray-500 rtl:text-right dark:text-gray-400 ${theme === "dark" ? "dark-mode" : ""}`}
                dir={dir}
              >
                <thead
                  className={`sticky top-0 z-10 border-b border-slate-200 text-xs text-slate-700 ${
                    theme === "dark" ? "border-gray-700 bg-gray-800 text-gray-200" : "bg-slate-50"
                  }`}
                >
                  <tr>
                    <th className="w-[44px] min-w-[44px] px-0 py-3.5 text-center" />
                    <th className="w-[90px] px-2 py-3.5 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500">
                      {t.bulkExportIdLabel ?? "Export ID"}
                    </th>
                    <th className="px-2 py-3.5 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500 whitespace-nowrap">
                      {t.bulkExportActivityLabel ?? "Activity"}
                    </th>
                    <th className="px-2 py-3.5 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500 whitespace-nowrap">
                      {t.dateRange ?? "Date Range"}
                    </th>
                    <th className="px-2 py-3.5 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500 whitespace-nowrap">
                      {t.accno ?? "Account No"}
                    </th>
                    <th className="w-[200px] px-2 py-3.5 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500">
                      {t.status ?? "Status"}
                    </th>
                    <th className="w-[190px] px-2 py-3.5 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500 whitespace-nowrap">
                      {t.bulkExportFailedAccountsColumn ?? "Failed Accounts"}
                    </th>
                    <th className="px-2 py-3.5 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500 whitespace-nowrap">
                      {t.bulkExportRequestedAt ?? "Requested"}
                    </th>
                    <th className="w-[160px] px-2 py-3.5 text-center text-xs font-semibold uppercase tracking-[0.04em] text-slate-500">
                      {t.bulkExportActionColumn ?? "Action"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {exportList.length === 0 ? (
                    <tr>
                      <td colSpan={columnCount} className="py-16 align-middle">
                        <div className="flex flex-col items-center justify-center text-center">
                          <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                            <Inbox size={22} />
                          </div>
                          <div className="text-sm font-semibold text-slate-800">
                            {isLoading
                              ? t.loading ?? "Loading..."
                              : t.bulkExportNoHistory ?? "No exports yet"}
                          </div>
                          <div className="mt-1 max-w-xs text-xs text-slate-400">
                            {t.bulkExportNoHistoryHelp ??
                              "Bulk PDF exports you request will show up here."}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    exportList.map((item) => {
                      const statusMeta = getBulkExportStatusMeta(item?.status, langMode);
                      const percentage = clampPercentage(item?.progressPercentage);
                      const isActive = isBulkExportActive(item?.status);
                      const isExpanded = expandedExportId === item?.exportId;
                      const isFocused = focusedExportId === item?.exportId;
                      const accountNoCount =
                        Number(item?.accountNoCount) ||
                        (Array.isArray(item?.accountNos) ? item.accountNos.length : 0);
                      const failedAccountNos = Array.isArray(item?.failedAccountNos)
                        ? item.failedAccountNos.filter(Boolean)
                        : [];

                      return (
                        <React.Fragment key={item?.exportId}>
                          <tr
                            className={`border-b border-slate-100 text-xs transition-colors duration-150 ${
                              theme === "dark"
                                ? "dark-mode"
                                : theme === "high-contrast"
                                  ? "high-contrast"
                                  : isFocused
                                    ? "bg-blue-50/70"
                                    : "bg-white hover:bg-slate-50/70"
                            }`}
                          >
                            <td className="w-[44px] px-0 py-2.5 text-center">
                              <button
                                type="button"
                                onClick={() => setExpandedExportId(isExpanded ? null : item?.exportId)}
                                aria-expanded={isExpanded}
                                aria-label={t.bulkExportToggleDetails ?? "Show export details"}
                                className="inline-flex h-6 w-6 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                              >
                                <ChevronDown
                                  size={14}
                                  className={isExpanded ? "rotate-180 transition-transform" : "transition-transform"}
                                />
                              </button>
                            </td>
                            <td className="px-2 py-2.5 font-mono text-xs text-[#70788f]">#{item?.exportId}</td>
                            <td className="px-2 py-2.5 text-xs font-medium text-slate-700">
                              {item?.workflowName || item?.workflowId || "-"}
                            </td>
                            <td className="px-2 py-2.5 text-xs text-slate-600 whitespace-nowrap">
                              <span className="rtl-keep-ltr">
                                {formatBulkExportDateRange(item?.fromDate, item?.toDate)}
                              </span>
                            </td>
                            <td className="px-2 py-2.5 text-xs text-slate-600">
                              {accountNoCount > 0
                                ? fillTemplate(t.bulkExportSelectedAccounts ?? "{count} selected", {
                                    count: accountNoCount,
                                  })
                                : t.bulkExportAllAccountsInRange ?? "All accounts in the date range"}
                            </td>
                            <td className="w-[200px] px-2 py-2.5">
                              <span
                                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${statusMeta.badgeClass}`}
                              >
                                {isActive && <Loader2 size={10} className="animate-spin" />}
                                {statusMeta.label}
                              </span>
                              <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                                <div
                                  className={`h-full rounded-full ${statusMeta.barClass} transition-[width] duration-500`}
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                              <p className="mt-1 text-[10px] text-slate-500">
                                {fillTemplate(t.bulkExportProcessedOfTotal ?? "{processed} of {total} PDFs", {
                                  processed: Number(item?.processedCount) || 0,
                                  total: Number(item?.totalCount) || 0,
                                })}
                              </p>
                            </td>
                            <td className="w-[190px] px-2 py-2.5">
                              {failedAccountNos.length > 0 ? (
                                <div className="flex flex-col items-start gap-1">
                                  <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                                    <AlertTriangle size={10} />
                                    {fillTemplate(
                                      t.bulkExportFailedAccountsCount ?? "{count} failed",
                                      { count: failedAccountNos.length }
                                    )}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      downloadFailedAccountsExcel(failedAccountNos, item?.exportId)
                                    }
                                    className="inline-flex items-center gap-1 text-[11px] font-medium text-[#236DB4] hover:underline"
                                    title={
                                      t.bulkExportDownloadFailedExcelTooltip ??
                                      "Download the failed account numbers as Excel"
                                    }
                                  >
                                    <FileSpreadsheet size={12} />
                                    {t.bulkExportDownloadFailedExcel ?? "Download Excel"}
                                  </button>
                                </div>
                              ) : (
                                <span className="text-[11px] text-slate-400">
                                  {Number(item?.failedCount) > 0
                                    ? fillTemplate(
                                        t.bulkExportFailedAccountsCount ?? "{count} failed",
                                        { count: Number(item.failedCount) }
                                      )
                                    : t.bulkExportNoFailedAccounts ?? "None"}
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-2.5 text-xs text-slate-600 whitespace-nowrap">
                              <span className="rtl-keep-ltr">
                                {formatBulkExportTimestamp(item?.requestedAt)}
                              </span>
                            </td>
                            <td className="w-[160px] px-2 py-2.5 text-center">
                              {isActive ? (
                                <CommonButton colorIntent="primary"
                                  variant="outline"
                                  type="button"
                                  onClick={() => focusExport(item?.exportId)}
                                  disabled={isFocused}
                                  tooltipText={t.bulkExportTrackTooltip ?? "Watch this export's progress"}
                                  tooltipPosition="top"
                                  className="inline-flex h-8 min-h-[32px] items-center justify-center gap-1.5 text-xs font-medium sm:min-w-[110px]"
                                >
                                  <Loader2 size={13} className="shrink-0 animate-spin" />
                                  <span className="truncate">{t.bulkExportTrack ?? "Track"}</span>
                                </CommonButton>
                              ) : item?.fileUrl ? (
                                <CommonButton colorIntent="primary"
                                  variant="primary"
                                  type="button"
                                  onClick={() => handleDownload(item)}
                                  disabled={!hasPDFDownloadAccess}
                                  tooltipText={t.bulkExportDownloadZipTooltip ?? "Download the prepared ZIP"}
                                  tooltipPosition="top"
                                  className="inline-flex h-8 min-h-[32px] items-center justify-center gap-1.5 text-xs font-medium sm:min-w-[110px]"
                                >
                                  <Download size={13} className="shrink-0" />
                                  <span className="truncate">{t.bulkExportDownloadZip ?? "Download ZIP"}</span>
                                </CommonButton>
                              ) : (
                                <span className="text-[11px] text-slate-400">
                                  {t.bulkExportNoFileShort ?? "No file"}
                                </span>
                              )}
                            </td>
                          </tr>

                          {isExpanded && (
                            <tr className={theme === "dark" ? "dark-mode" : "bg-slate-50/60"}>
                              <td colSpan={columnCount} className="px-4 py-3">
                                <div className="space-y-2.5">
                                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                                    <div>
                                      <p className="text-[10px] font-semibold uppercase tracking-[0.04em] text-slate-400">
                                        {t.bulkExportTotalLabel ?? "Total"}
                                      </p>
                                      <p className="text-xs font-semibold text-slate-700">
                                        {Number(item?.totalCount) || 0}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-[10px] font-semibold uppercase tracking-[0.04em] text-slate-400">
                                        {t.bulkExportSuccessLabel ?? "Succeeded"}
                                      </p>
                                      <p className="text-xs font-semibold text-emerald-600">
                                        {Number(item?.successCount) || 0}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-[10px] font-semibold uppercase tracking-[0.04em] text-slate-400">
                                        {t.bulkExportFailedLabel ?? "Failed"}
                                      </p>
                                      <p className="text-xs font-semibold text-amber-600">
                                        {Number(item?.failedCount) || 0}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-[10px] font-semibold uppercase tracking-[0.04em] text-slate-400">
                                        {t.bulkExportCompletedAt ?? "Completed"}
                                      </p>
                                      <p className="text-xs font-semibold text-slate-700">
                                        {formatBulkExportTimestamp(item?.completedAt)}
                                        {formatFileSize(item?.fileSizeBytes)
                                          ? ` · ${formatFileSize(item.fileSizeBytes)}`
                                          : ""}
                                      </p>
                                    </div>
                                  </div>

                                  {item?.errorMessage && (
                                    <p className="rounded-lg border border-red-200 bg-red-50/70 px-3 py-2 text-[11px] text-red-700">
                                      {item.errorMessage}
                                    </p>
                                  )}

                                  <FailedAccountsPanel
                                    failedCount={item?.failedCount}
                                    failedAccountNos={item?.failedAccountNos}
                                    exportId={item?.exportId}
                                    langMode={langMode}
                                  />

                                  {Array.isArray(item?.accountNos) && item.accountNos.length > 0 && (
                                    <div>
                                      <p className="text-[10px] font-semibold uppercase tracking-[0.04em] text-slate-400">
                                        {t.accno ?? "Account No"}
                                      </p>
                                      <div className="mt-1 flex max-h-28 flex-wrap gap-1 overflow-y-auto">
                                        {item.accountNos.map((accountNo) => (
                                          <span
                                            key={accountNo}
                                            className="rtl-keep-ltr rounded-md border border-slate-200 bg-white px-1.5 py-0.5 font-mono text-[11px] text-slate-600"
                                          >
                                            {accountNo}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </TableScrollShell>
          </div>
        </div>
      </div>

    </div>
  );
};

export default PdfExportHistory;
